import { apiClient } from './api'

export interface Recommendation {
  id: string
  yardId?: string | number
  yardName?: string
  userId: string | number
  userName: string
  title: string
  description: string
  recommendedAction?: 'inspection' | 'treatment' | 'harvest' | 'movement' | 'feeding' | 'splitting' | 'custom'
  status: 'pending' | 'accepted' | 'rejected'
  source: 'voice_assistant' | 'manual' | 'system'
  sourceLanguage?: string
  translations?: Record<string, { title: string; description: string }>
  createdAt: string
  resolvedAt?: string
  resolvedBy?: string | number
  taskId?: string
}

export interface TranslationResponse {
  sourceLanguage: string | null
  target: string
  title: string
  description: string
  cached: boolean
}

/**
 * Translate a recommendation into the target language (auto-detects source).
 */
export function translateRecommendation(id: string, target: string) {
  return apiClient.post<TranslationResponse>(`/recommendations/${id}/translate`, { target })
}

export interface CreateRecommendationPayload {
  yardId?: string | number
  yardName?: string
  userId: string | number
  userName: string
  title: string
  description: string
  recommendedAction?: 'inspection' | 'treatment' | 'harvest' | 'movement' | 'feeding' | 'splitting' | 'custom'
  source?: 'voice_assistant' | 'manual' | 'system'
}

export interface AcceptRecommendationPayload {
  createTask: boolean
  taskData?: {
    title?: string
    description?: string
    dueDate?: string
    type?: string
    userId?: string | number
    yardId?: string
    teamId?: string
  }
}

/**
 * Gets recommendations
 * @param params - Query params
 * @returns Promise with recommendation list
 */
export function getRecommendations(params?: { status?: string; yardId?: string | number }) {
  return apiClient.get<Recommendation[]>('/recommendations', { params })
}

/**
 * Creates a new recommendation
 * @param payload - Recommendation payload
 * @returns Promise with created recommendation
 */
export function createRecommendation(payload: CreateRecommendationPayload) {
  return apiClient.post<Recommendation>('/recommendations', payload)
}

/**
 * Accepts a recommendation and optionally creates a task
 * @param id - Recommendation ID
 * @param payload - Accept payload with optional task data
 * @returns Promise with updated recommendation and optional task
 */
export function acceptRecommendation(id: string, payload: AcceptRecommendationPayload) {
  return apiClient.post<{ recommendation: Recommendation; task: any | null }>(`/recommendations/${id}/accept`, payload)
}

/**
 * Rejects a recommendation
 * @param id - Recommendation ID
 * @returns Promise with updated recommendation
 */
export function rejectRecommendation(id: string) {
  return apiClient.post<Recommendation>(`/recommendations/${id}/reject`)
}

/**
 * Deletes a recommendation
 * @param id - Recommendation ID
 * @returns Promise void
 */
export function deleteRecommendation(id: string) {
  return apiClient.delete(`/recommendations/${id}`)
}
